import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, MenuItem, TextField } from '@mui/material'

import { FormError } from '../../types/common.types'
import '../../styles/bank-accounts-management.css'
import { BankAccount, Bank } from '../../types/user.types'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { argentinianBanksData, brazilianBanksData, chileanBanksData, peruBanksData } from '../customer-companies-management/lib'
import { mexicanBankAccountType, customersBankAccountType, argentinianBankAccountType, chileanBankAccountType, peruvianBankAccountType, brazilianBankAccountType } from '../../lib/common.lib'

const BankAccountForm = ({
  errors,
  banksData,
  bankAccountData,
  mainBankAccountAsDefault,
  handleSetBankAccountData
}: {
  errors: FormError
  banksData: Array<Bank>
  mainBankAccountAsDefault: boolean
  bankAccountData: Partial<BankAccount>
  handleSetBankAccountData: (bankAccountDataUpdated: BankAccount) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const { t } = useTranslation(['bank-accounts-management'])

  const [bankAccountTypesToSelect, setBankAccountTypesToSelect] = useState<{ [key: string]: string }>({})
  const [otherCountriesBanksData, setOtherCountriesBanksData] = useState<Array<{ name: string; code: string }>>([])

  useEffect(() => {
    if (selectedCountry === 'COL') {
      setBankAccountTypesToSelect(customersBankAccountType)
    }

    if (selectedCountry === 'MEX') {
      setBankAccountTypesToSelect(mexicanBankAccountType)
    }

    if (selectedCountry === 'ARG') {
      const banksDataSorted = argentinianBanksData.sort((a, b) => a.name.localeCompare(b.name))

      setOtherCountriesBanksData(banksDataSorted)
      setBankAccountTypesToSelect(argentinianBankAccountType)
    }

    if (selectedCountry === 'CHI') {
      const banksDataSorted = chileanBanksData.sort((a, b) => a.name.localeCompare(b.name))

      setOtherCountriesBanksData(banksDataSorted)
      setBankAccountTypesToSelect(chileanBankAccountType)
    }

    if (selectedCountry === 'PER') {
      const banksDataSorted = peruBanksData.sort((a, b) => a.name.localeCompare(b.name))

      setOtherCountriesBanksData(banksDataSorted)
      setBankAccountTypesToSelect(peruvianBankAccountType)
    }

    if (selectedCountry === 'BRA') {
      const banksDataSorted = brazilianBanksData.sort((a, b) => a.name.localeCompare(b.name))

      setOtherCountriesBanksData(banksDataSorted)
      setBankAccountTypesToSelect(brazilianBankAccountType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="add-new-bank-account-form-container">
      <div className="add-new-bank-account-form-fields-container">
        <div className="add-new-bank-account-form-field-container">
          <TextField
            select
            fullWidth
            variant="standard"
            sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            inputProps={{
              style: { textAlign: 'center' }
            }}
            label={t('newBankAccountModal.bankAccountForm.fields.accountType')}
            error={!!errors.bankAccountType}
            helperText={errors.bankAccountType}
            value={bankAccountData.bankAccountType ?? ''}
            onChange={(event) => {
              const bankAccountDataUpdated =
                Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
              bankAccountDataUpdated.bankAccountType = event.target.value

              handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
            }}
          >
            <MenuItem
              disabled
              value=""
            >
              {t('newBankAccountModal.bankAccountForm.fields.select')}
            </MenuItem>
            {Object.keys(bankAccountTypesToSelect).map((bankAccountType: string) => {
              return (
                <MenuItem
                  key={bankAccountType}
                  value={bankAccountType}
                >
                  {(bankAccountTypesToSelect as any)[bankAccountType].toUpperCase()}
                </MenuItem>
              )
            })}
          </TextField>
        </div>
        {selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb' && (
          <div className="add-new-bank-account-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('newBankAccountModal.bankAccountForm.fields.bankName')}
              error={!!errors.bankName}
              helperText={errors.bankName}
              value={Object.keys(bankAccountData).length > 0 && bankAccountData.bankName && bankAccountData.bankAchCode ? `${bankAccountData.bankName}-${bankAccountData.bankAchCode}` : ''}
              onChange={(event) => {
                const bankAccountDataUpdated =
                  Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
                bankAccountDataUpdated.bankName = event.target.value.split('-')[0]
                bankAccountDataUpdated.bankAchCode = event.target.value.split('-')[1]

                handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newBankAccountModal.bankAccountForm.fields.select')}
              </MenuItem>
              {banksData.map((bankData: Bank) => {
                return (
                  <MenuItem
                    key={bankData.achCode}
                    value={`${bankData.name}-${bankData.achCode}`}
                  >
                    {bankData.name.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
        {(selectedCountry === 'ARG' || selectedCountry === 'CHI' || selectedCountry === 'PER' || selectedCountry === 'BRA') && (
          <div className="add-new-bank-account-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('newBankAccountModal.bankAccountForm.fields.bankName')}
              error={!!errors.bankName}
              helperText={errors.bankName}
              value={Object.keys(bankAccountData).length > 0 && bankAccountData.bankName && bankAccountData.bankAchCode ? `${bankAccountData.bankName}-${bankAccountData.bankAchCode}` : ''}
              onChange={(event) => {
                const bankAccountDataUpdated =
                  Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
                bankAccountDataUpdated.bankName = event.target.value.split('-')[0]
                bankAccountDataUpdated.bankAchCode = event.target.value.split('-')[1]

                handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newBankAccountModal.bankAccountForm.fields.select')}
              </MenuItem>
              {otherCountriesBanksData.map((bankData: { name: string; code: string }) => {
                return (
                  <MenuItem
                    key={bankData.code}
                    value={`${bankData.name}-${bankData.code}`}
                  >
                    {bankData.name.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
        <div className="add-new-bank-account-form-field-container">
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            label={t('newBankAccountModal.bankAccountForm.fields.bankAccountNumber')}
            error={!!errors.bankAccountNumber}
            helperText={errors.bankAccountNumber}
            value={bankAccountData.bankNumber ?? ''}
            onChange={(event) => {
              const bankAccountDataUpdated =
                Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
              bankAccountDataUpdated.bankNumber = event.target.value

              handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
            }}
          />
        </div>
        {selectedCountry === 'BRA' && (
          <div className="add-new-bank-account-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('newBankAccountModal.bankAccountForm.fields.pix')}
              error={!!errors.pixKey}
              helperText={errors.pixKey}
              value={bankAccountData.pix ?? ''}
              onChange={(event) => {
                const bankAccountDataUpdated =
                  Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
                bankAccountDataUpdated.pix = event.target.value

                handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
              }}
            />
          </div>
        )}
      </div>
      {!mainBankAccountAsDefault && (
        <div className="add-new-bank-account-form-checkbox-fields-container">
          <Checkbox
            checked={bankAccountData.bankAccountMain ?? false}
            onClick={(event) => {
              event.stopPropagation()
              const bankAccountDataUpdated =
                Object.keys(bankAccountData).length > 0 ? { ...bankAccountData } : { bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: bankAccountData.bankAccountMain ?? false }
              bankAccountDataUpdated.bankAccountMain = bankAccountData.bankAccountMain ? !bankAccountData.bankAccountMain : true

              handleSetBankAccountData(bankAccountDataUpdated as BankAccount)
            }}
          />
          <div className="add-new-bank-account-form-checkbox-text">{t('newBankAccountModal.bankAccountForm.fields.selectAsMainAccount')}</div>
        </div>
      )}
    </div>
  )
}

export default BankAccountForm
